import { MembershipTypes } from '@/enums/member'
import { Orientations } from '@/enums/products'

/**
 * Groups the product sizes from the product groups
 *
 * @param productGroups
 */
export const groupProductSizes = productGroups => productGroups.reduce((acc, cur) => ({
  [cur._id]: cur.productGroupSizes.edges.map(({ node }) => ({
    value: node.size._id,
    label: node.size.label,
    _id: node.size._id
  })),
  ...acc
}), {})

/**
 * Groups the membershiptype sizes from the membershiptypes
 *
 * @param productGroups
 */
export const groupMembershipTypesSizes = membershipTypes => membershipTypes.reduce((acc, cur) => {
  const productGroups = cur.productGroups.map(productGroup => ({
    [productGroup.reference]: productGroup.productGroupSizes.map(({ size, products = [], reference }) => ({
      value: reference,
      label: size.label,
      inStock: products?.length > 0
    }))
  }))

  const mergedObject = productGroups.reduce((result, currentObject) => {
    const key = Object.keys(currentObject)[0]
    result[key] = currentObject[key]
    return result
  }, {})

  return {
    ...mergedObject,
    ...acc
  }
}, {})

/**
 * Groups the membershiptype sizes from the membershiptypes for a specific design
 *
 * @param productGroups
 */
export const groupMembershipTypesSizesForDesign = (membershipTypes, designSlug) => membershipTypes.reduce((acc, cur) => {
  const productGroups = cur.productGroups.map(productGroup => ({
    [productGroup.reference]: productGroup.productGroupSizes.map(({ size, products = [], reference }) => ({
      value: reference,
      label: size.label?.replace('Shirt', '') || '',
      inStock: products.some(product => product.design.reference === designSlug)
    }))
  }))

  const mergedObject = productGroups.reduce((result, currentObject) => {
    const key = Object.keys(currentObject)[0]
    result[key] = currentObject[key]
    return result
  }, {})

  return {
    ...mergedObject,
    ...acc
  }
}, {})

export const groupMembershipTypeAvailableDesigns = (membershipTypes, sizeReference, productGroup) => {
  return membershipTypes.reduce((acc, membershipType) => {
    let membershipTypeReference = `${productGroup}-membership`

    if ([Orientations.womenbralette, Orientations.womenhipster].includes(productGroup)) {
      membershipTypeReference = MembershipTypes.women
    }

    if (membershipType.reference === membershipTypeReference) {
      const pg = membershipType.productGroups.find(pgs => pgs.reference === productGroup)

      if (pg) {
        const correctSize = pg.productGroupSizes.find(productGroupSize => productGroupSize.reference === sizeReference)

        if (correctSize) {
          return correctSize.products.map(product => ({ name: product.design.name, reference: product.design.reference, productGroup: pg.reference, designGroup: product.design.designGroup, partnershipDesign: product.design.partnershipDesign || false, isFeatured: product.design.isFeatured || false })).filter(product => !product.partnershipDesign)
        }
      }
    }

    return acc
  }, [])
}

export const getMembershipTypeDesignWithMostStock = (membershipTypes, productGroup) => {
  return membershipTypes.reduce((acc, membershipType) => {
    const pg = membershipType.productGroups.find(pgs => pgs.reference === productGroup)

    if (pg) {
      const designHighestCounts = {}
      const designObjects = {}

      pg.productGroupSizes.forEach((productGroupSize) => {
        productGroupSize.products.forEach((product) => {
          if (['highest', 'high'].includes(product.stockOrder)) {
            designHighestCounts[product.design.reference] = (designHighestCounts[product.design.reference] || 0) + 1
            designObjects[product.design.reference] = product.design
          }
        })
      })

      let maxHighestCount = 0
      let designWithMostHighest = null

      Object.entries(designHighestCounts).forEach(([designRef, count]) => {
        if (count > maxHighestCount) {
          maxHighestCount = count
          designWithMostHighest = designRef
        }
      })

      return maxHighestCount > acc.maxHighestCount
        ? { designWithMostHighest: designObjects[designWithMostHighest], maxHighestCount }
        : acc
    }

    return acc
  }, { maxHighestCount: 0, designWithMostHighest: null }).designWithMostHighest
}

export const givenProductGroupReturnFirstAvailableSizeReference = (membershipTypes, productGroup) => {
  return membershipTypes.reduce((acc, membershipType) => {
    let membershipTypeReference = `${productGroup}-membership`

    if ([Orientations.womenbralette, Orientations.womenhipster].includes(productGroup)) {
      membershipTypeReference = MembershipTypes.women
    }

    if (membershipType.reference === membershipTypeReference) {
      const pg = membershipType.productGroups.find(pgs => pgs.reference === productGroup)

      if (pg) {
        const firstAvailableSize = pg.productGroupSizes.find(productGroupSize => productGroupSize.products.length > 0)

        if (firstAvailableSize) {
          return firstAvailableSize.reference
        }
      }
    }

    return acc
  }, null)
}

export const getMembershipTypeDesignWithMostStockInSpecificSize = (membershipTypes, productGroup, sizeReference) => {
  return membershipTypes.reduce((acc, membershipType) => {
    const pg = membershipType.productGroups.find(pgs => pgs.reference === productGroup)

    if (pg) {
      const sizeGroup = pg.productGroupSizes.find(size => size.reference === sizeReference)

      if (sizeGroup) {
        const highestStockProducts = sizeGroup.products.filter(product =>
          product.stockOrder === 'highest'
        )

        if (highestStockProducts.length > 0) {
          return highestStockProducts[0].design
        }
      }
    }

    return acc
  }, {})
}

export const getMembershipTypeIsDesignAvailableInSpecificSize = (membershipTypes, productGroup, sizeReference, designSlug) => {
  return membershipTypes.reduce((acc, membershipType) => {
    const pg = membershipType.productGroups.find(pgs => pgs.reference === productGroup)

    if (pg) {
      const sizeGroup = pg.productGroupSizes.find(size => size.reference === sizeReference)

      if (sizeGroup) {
        return sizeGroup.products.some(product => product.design.reference === designSlug && product.stockOrder !== 'none')
      }
    }

    return acc
  }, false)
}

/**
 * Checks wether it's a boxer or sock
 *
 * @param productGroupSizeId
 */

export const isBoxerOrSockOrWomen = productGroup => Object.entries(Orientations).some(([_, value]) => productGroup.reference === value)

export const transformMembershipTypeOrientationsToOptions = (membershipTypes) => {
  return membershipTypes.map(membershipType => ({
    ...membershipType,
    value: membershipType.reference,
    label: membershipType.name
  }))
}

export const getProductGroupFromUrlParam = (param = '') => {
  const membershipType = (param || '').toLowerCase().replace('-membership', '')

  if (Object.values(MembershipTypes).map(type => type.toLowerCase().replace('-membership', '')).includes(membershipType)) {
    return membershipType
  }

  return MembershipTypes.menOriginals.replace('-membership', '')
}
